// FILE: projectEnvironments.logic.ts
// Purpose: Pure helpers behind the "Del proyecto" tab — how a sync state reads,
//          which environment a name refers to, and whether a name is one the
//          server will accept — kept apart from the component so they can be
//          tested without React.
// Exports: `syncPill`, `trialPill`, `environmentPill`, `describeActiveTrial`,
//          `describeClonedFrom`, `validateEnvironmentName`,
//          `projectEnvironmentsOf`, `personalEnvironmentsOf`,
//          `trialCandidatesOf`, `environmentTryingPullRequest`,
//          `describeActiveOperation`, `describeLastModification`,
//          `baselineSnapshotOf`.

import {
  PROJECT_ENVIRONMENT_NAME_MAX_CHARS,
  PROJECT_ENVIRONMENT_NAME_PATTERN,
  type ProjectEnvironmentModificationKind,
  type ProjectEnvironmentSyncState,
} from "@taskin-code/contracts";

import type {
  AdminEnvironmentRow,
  AdminEnvironmentSnapshotRow,
} from "../../auth/EnvironmentsClient";

export interface SyncPill {
  readonly label: string;
  /** Tailwind classes for the pill body. */
  readonly className: string;
  /** Tailwind classes for the leading dot. */
  readonly dotClassName: string;
  /** Long form, for the pill's `title`. */
  readonly hint: string;
}

/**
 * The pill that says how the environment stands against its base branch.
 *
 * `unknown` is grey and says so out loud, never green: the whole point of the
 * state existing is that a repository the server could not reach must not read
 * as "up to date". `diverged` keeps the count of missing commits — the person
 * still wants to know how far behind it is — but leads with the divergence,
 * because that is the part a fast-forward cannot fix.
 */
export function syncPill(input: {
  readonly syncState: ProjectEnvironmentSyncState;
  readonly behindBy: number | null;
}): SyncPill {
  switch (input.syncState) {
    case "in-sync":
      return {
        label: "Al día",
        className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
        dotClassName: "bg-emerald-500",
        hint: "El entorno tiene todos los commits de su rama base.",
      };
    case "behind": {
      const count = input.behindBy ?? 0;
      return {
        label: count === 1 ? "Por detrás 1 commit" : `Por detrás ${count} commits`,
        className: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
        dotClassName: "bg-amber-500",
        hint: "La rama base tiene commits que este entorno todavía no.",
      };
    }
    case "diverged":
      return {
        label:
          input.behindBy !== null && input.behindBy > 0
            ? `Divergido · por detrás ${input.behindBy}`
            : "Divergido",
        className: "bg-amber-500/15 text-amber-600 dark:text-amber-300",
        dotClassName: "bg-amber-500",
        hint: "El entorno tiene commits propios que no están en la rama base: actualizar exige resetear, y eso se confirma en el hilo.",
      };
    case "unknown":
      return {
        label: "Desconocido",
        className: "bg-muted/50 text-muted-foreground",
        dotClassName: "bg-muted-foreground/40",
        hint: "No se pudo medir la distancia con la rama base — normalmente, el remoto no responde. No significa que esté al día.",
      };
  }
}

/**
 * The pill for an environment that is wearing a pull request.
 *
 * Blue, and it replaces the freshness pill rather than sitting next to it. The
 * two would contradict each other on purpose — an environment with a pull
 * request merged on top *is* diverged from its base branch — and "Divergido" is
 * a true answer to a question nobody asked. What the person needs to know in one
 * glance is that the preview they are about to open is not the product: it is
 * PR #131.
 */
export function trialPill(prNumber: number): SyncPill {
  return {
    label: `Probando PR #${prNumber}`,
    className: "bg-sky-500/15 text-sky-600 dark:text-sky-300",
    dotClassName: "bg-sky-500",
    hint: `Este entorno tiene el PR #${prNumber} montado encima de su rama base. Hasta que se vuelva a la base, su preview muestra ese PR y no se puede actualizar.`,
  };
}

/** The one pill the card shows: the trial when there is one, freshness otherwise. */
export function environmentPill(input: {
  readonly activeTrial: { readonly prNumber: number } | null;
  readonly syncState: ProjectEnvironmentSyncState;
  readonly behindBy: number | null;
}): SyncPill {
  return input.activeTrial === null
    ? syncPill({ syncState: input.syncState, behindBy: input.behindBy })
    : trialPill(input.activeTrial.prNumber);
}

/**
 * «PR #131 · montado por Ana · hace 20 minutos».
 *
 * Who and since when, because the trial is the one state of a shared
 * environment that somebody else put it in on purpose and that blocks everybody
 * else's work — so "ask that person before you undo it" has to be actionable,
 * and the thread the card links to is where they explained why.
 */
export function describeActiveTrial(input: {
  readonly prNumber: number;
  /** Null when auth is off, or the directory has not loaded yet. */
  readonly actorName: string | null;
  /** Already formatted by the caller, which owns the one relative-time helper. */
  readonly when: string | null;
}): string {
  return [
    `PR #${input.prNumber} montado`,
    input.actorName === null ? null : `por ${input.actorName}`,
    input.when === null ? null : input.when,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/**
 * The project environments «Probar sobre QA» may be pointed at.
 *
 * `ready` only, and free of another trial: an environment still provisioning has
 * no worktree to merge into, and one already wearing a pull request would refuse
 * on the server. Offering either would be offering a refusal.
 */
export function trialCandidatesOf(
  rows: ReadonlyArray<AdminEnvironmentRow>,
): ReadonlyArray<AdminEnvironmentRow> {
  // `?? null` rather than `=== null`: a payload cached by a build older than
  // 061 has no such field, and reading that as "busy" would quietly leave the
  // button with nothing to offer and no way to find out why.
  return projectEnvironmentsOf(rows).filter(
    (row) => row.status === "ready" && (row.activeTrial ?? null) === null,
  );
}

/**
 * The environment already trying THIS pull request, if any.
 *
 * What turns "Probar sobre QA" into "Ver preview" + "Volver a base": pressing
 * the first again would be refused by the server with "ya se está probando", and
 * a button whose only outcome is that message is worse than the two buttons the
 * person actually wants.
 */
export function environmentTryingPullRequest(
  rows: ReadonlyArray<AdminEnvironmentRow>,
  prNumber: number,
): AdminEnvironmentRow | null {
  return projectEnvironmentsOf(rows).find((row) => row.activeTrial?.prNumber === prNumber) ?? null;
}

/**
 * «Clonado de `qa` el … (SHA …)», while the origin still exists.
 *
 * Returns `null` when the environment was not cloned, and falls back to the
 * bare id when the origin has been destroyed: the fact that this environment
 * was copied from something does not stop being true when the something is
 * gone, and hiding it would make its data unexplainable.
 */
export function describeClonedFrom(
  row: AdminEnvironmentRow,
  known: ReadonlyArray<AdminEnvironmentRow>,
): string | null {
  if (row.clonedFromEnvironmentId === null) return null;
  const origin = known.find((candidate) => candidate.id === row.clonedFromEnvironmentId) ?? null;
  const originName = origin?.name ?? origin?.branch ?? "un entorno que ya no existe";
  const when = new Date(row.createdAt);
  const date = Number.isNaN(when.getTime()) ? null : when.toLocaleDateString();
  const sha = row.clonedFromSha === null ? null : row.clonedFromSha.slice(0, 7);
  return [
    `Clonado de ${originName}`,
    date === null ? null : `el ${date}`,
    sha === null ? null : `(SHA ${sha})`,
  ]
    .filter((part): part is string => part !== null)
    .join(" ");
}

/**
 * The same rule the server applies, applied before the request.
 *
 * Not a substitute for it — the decider is the authority, and it also refuses
 * duplicates — but a name typed with a capital letter should be corrected while
 * it is being typed, not after a round trip that has already been refused.
 */
export function validateEnvironmentName(raw: string, taken: ReadonlyArray<string>): string | null {
  const name = raw.trim();
  if (name.length === 0) return "Ponle nombre al entorno.";
  if (name.length > PROJECT_ENVIRONMENT_NAME_MAX_CHARS) {
    return `Como mucho ${PROJECT_ENVIRONMENT_NAME_MAX_CHARS} caracteres.`;
  }
  if (!PROJECT_ENVIRONMENT_NAME_PATTERN.test(name)) {
    return "Sólo minúsculas, dígitos y guiones sueltos: el nombre va a una rama y a un hostname.";
  }
  if (taken.includes(name)) return `Este proyecto ya tiene un entorno llamado ${name}.`;
  return null;
}

/** Live project environments, oldest first — the order they were created in. */
export function projectEnvironmentsOf(
  rows: ReadonlyArray<AdminEnvironmentRow>,
): ReadonlyArray<AdminEnvironmentRow> {
  return rows
    .filter((row) => row.kind === "project" && row.deletedAt === null)
    .toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));
}

/** Live personal environments — what the Personales tab has always listed. */
export function personalEnvironmentsOf(
  rows: ReadonlyArray<AdminEnvironmentRow>,
): ReadonlyArray<AdminEnvironmentRow> {
  return rows.filter((row) => row.kind === "personal" && row.deletedAt === null);
}

/**
 * «Actualizando… por Ana», while somebody holds the environment's lock.
 *
 * The point of naming the person is that the alternative — a greyed-out button
 * with no explanation — reads as broken. An environment takes one operation at
 * a time by design, so "you cannot press this" is nearly always "somebody else
 * already is", and that is worth one sentence.
 *
 * The verbs are the server's `EnvironmentOperationKind`. An unknown one is not
 * an error: the lock is shared with the git operations, and a name this build
 * has not heard of still means the environment is busy.
 */
const OPERATION_VERB: Readonly<Record<string, string>> = {
  checkout: "Cambiando de rama…",
  merge: "Actualizando…",
  "reset-to-base": "Reseteando a la rama base…",
  "sync-base": "Actualizando…",
  "try-pr": "Montando el PR…",
  "back-to-base": "Volviendo a la rama base…",
  "run-setup": "Ejecutando el setup…",
  "copy-data": "Copiando datos…",
  "reset-data": "Reseteando datos…",
};

const UNKNOWN_OPERATION_VERB = "Trabajando en el entorno…";

export function describeActiveOperation(input: {
  readonly operation: string;
  /** Null when auth is off, or when the directory has not loaded yet. */
  readonly actorName: string | null;
}): string {
  const verb = OPERATION_VERB[input.operation] ?? UNKNOWN_OPERATION_VERB;
  return input.actorName === null ? verb : `${verb} por ${input.actorName}`;
}

/** How each kind of write reads in the "última modificación" line. */
export const MODIFICATION_KIND_LABEL: Readonly<Record<ProjectEnvironmentModificationKind, string>> =
  {
    data: "los datos",
    config: "la configuración",
  };

/**
 * «Última modificación: los datos, por Ana, hace 2 horas».
 *
 * Returns `null` when nothing has been recorded — which is every environment
 * created before migration 062, and any that nobody has written to since. An
 * empty line is better than "modificado por nadie, nunca", which invites the
 * reading that the feature is broken.
 *
 * `when` arrives already formatted so this stays a pure string builder and the
 * caller keeps its one relative-time implementation.
 */
export function describeLastModification(input: {
  readonly at: string | null;
  readonly kind: ProjectEnvironmentModificationKind | null;
  readonly authorName: string | null;
  readonly when: string | null;
}): string | null {
  if (input.at === null) return null;
  const what = input.kind === null ? "el entorno" : MODIFICATION_KIND_LABEL[input.kind];
  return [
    `Última modificación: ${what}`,
    input.authorName === null ? null : `por ${input.authorName}`,
    input.when === null ? null : input.when,
  ]
    .filter((part): part is string => part !== null)
    .join(", ");
}

/**
 * The environment's baseline copy — the one «Resetear datos» goes back to when
 * no other is named.
 *
 * Matched by label rather than by a flag because the label is what the snapshot
 * store actually records, and it is the same string the agent's tools match on.
 */
export function baselineSnapshotOf(
  snapshots: ReadonlyArray<AdminEnvironmentSnapshotRow>,
): AdminEnvironmentSnapshotRow | null {
  return snapshots.find((snapshot) => snapshot.label.toLowerCase().includes("baseline")) ?? null;
}
